import React from "react";
import styled, { css } from "styled-components";

import { breakpoints, colors, fontFamily, sectionRhythm } from "../../../theme";
import { PROJECT_JIFFY } from "../../utils/constants/projects";
import { projectImage } from "../../utils/cdn";
import ContactMeSection from "../contact-me-section";
import Testimonials from "../reviews-section/Testimonials";
import ContainerScroll from "../../components/container-scroll";
import { ProjectHero, caseStudyBodyStyles } from "./project-page-media";
import { POINTER_HOVER_MEDIA } from "../../styles/pointer-hover";
import { getLinkClassName } from "../../styles/global-styles";

const PageContainer = styled.div`
  width: calc(100vw - 48px);
  padding-top: ${sectionRhythm.paddingY.mobile}px;
  margin: 0 auto;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    width: 65%;
    max-width: 800px;
    padding-top: ${sectionRhythm.paddingY.desktop}px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 900px) {
    width: 60%;
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  font-family: ${fontFamily.extraBold};
  font-size: 24px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 44px;
    margin: 0 0 24px;
  }
`;

const ContentImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  border-radius: 18px;
  background: ${colors.white};
  margin: 1.5rem 0 0.5rem;
`;

const ImageCaption = styled.figcaption`
  display: block;
  font-size: 14px;
  text-align: center;
  color: ${colors.grey.darkMedium};
  margin-bottom: 1.5rem;
`;

const OverviewSection = styled.div`
  margin-top: 2rem;
  margin-bottom: 0;
  display: grid;
  gap: 1.5rem;
`;

const OverviewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
`;

const OverviewItem = styled.div`
  flex: 1;
  min-width: 140px;
`;

const OverviewLabel = styled.div`
  font-size: 14px;
  color: ${colors.grey.darkMedium};
  margin-bottom: 4px;
`;

const OverviewValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;

  a {
    color: inherit;
    word-break: break-all;

    @media ${POINTER_HOVER_MEDIA} {
      &:hover {
        color: ${colors.linkHover.light};
        text-decoration: underline;
      }
    }
  }
`;

const Body = styled.div`
  ${caseStudyBodyStyles}
`;

/* Diagram + decision styles (aligned with Condexo Pay) */

const boxHelperTypography = css`
  font-size: 14px;
  line-height: 1.45;
  font-weight: 600;
  color: ${colors.grey.darkMedium};
`;

const diagramHelperText = css`
  ${boxHelperTypography}
  display: block;
`;

const DecisionSection = styled.div`
  margin-top: 0;
`;

const DecisionBlock = styled.div`
  margin-top: 0;
  padding-top: 2rem;

  &:first-child {
    padding-top: 0;
  }

  & > h5 {
    margin-top: 0;
  }
`;

const DecisionTitle = styled.h5`
  margin: 0 0 0.75rem;
  font-family: ${fontFamily.extraBold};
  font-size: 16px;
  line-height: 1.3;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    font-size: 18px;
  }
`;

const DecisionQuestion = styled.p`
  margin: 1.25rem 0 1.5rem;
  font-weight: 600;
`;

const DecisionComparison = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin: 2rem 0;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DecisionCard = styled.div`
  padding: 1.25rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 10px;
  background: ${colors.white};

  h6 {
    margin: 0 0 0.75rem;
    font-family: ${fontFamily.extraBold};
    font-size: 15px;
    line-height: 1.3;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 16px;
    }
  }

  ul,
  ol {
    margin: 0;
    padding-left: 1.25rem;
    list-style: none;
  }

  li {
    ${boxHelperTypography}
    display: list-item;
    margin: 0 0 0.45rem 1rem;
    padding: 0;
    letter-spacing: -0.003em;
    list-style-position: outside;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 14px;
      line-height: 1.45;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul > li {
    list-style-type: disc;
  }

  ol > li {
    list-style-type: decimal;
  }
`;

const ArchitectureSplit = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin: 2rem 0 2.5rem;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
  }
`;

const ArchitectureBranch = styled.div`
  position: relative;
  padding-top: 0;
`;

const ArchitectureLabel = styled.div`
  margin-bottom: 0.8rem;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  text-transform: uppercase;
  color: ${colors.grey.darkMedium};
`;

const ArchitectureStack = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const ArchitectureStackItem = styled.div`
  padding: 0.85rem 1rem;
  border: 1px solid ${colors.grey.light};
  border-radius: 8px;
  background: ${colors.white};

  strong {
    display: block;
    font-size: 14px;
    font-weight: 700;
  }

  small {
    ${diagramHelperText}
    margin-top: 0.2rem;
  }
`;

/* Diagrams */

const CssPathSplit = () => (
  <ArchitectureSplit>
    <ArchitectureBranch>
      <ArchitectureLabel>Critical path</ArchitectureLabel>
      <ArchitectureStack>
        <ArchitectureStackItem>
          <strong>Main pack CSS</strong>
          <small>Render-blocking for first paint</small>
        </ArchitectureStackItem>
        <ArchitectureStackItem>
          <strong>Critical masthead SCSS</strong>
          <small>Synced into the main path</small>
        </ArchitectureStackItem>
      </ArchitectureStack>
    </ArchitectureBranch>
    <ArchitectureBranch>
      <ArchitectureLabel>Deferred path</ArchitectureLabel>
      <ArchitectureStack>
        <ArchitectureStackItem>
          <strong>Feature CSS packs</strong>
          <small>media=print → onload → all</small>
        </ArchitectureStackItem>
        <ArchitectureStackItem>
          <strong>Preload on cached PDP</strong>
          <small>preload as style → stylesheet</small>
        </ArchitectureStackItem>
      </ArchitectureStack>
    </ArchitectureBranch>
  </ArchitectureSplit>
);

const JiffyProjectSection = () => {
  const {
    client,
    services,
    industry,
    techStack,
    links,
    heading,
    media,
    roles,
    year,
  } = PROJECT_JIFFY;
  const yearLabel = year.length > 1 ? `${year[0]}-${year[1]}` : year[0];

  return (
    <>
      <PageContainer>
        <ContainerScroll
          titleComponent={<Title>{heading}</Title>}
          role={roles.join(", ")}
          year={yearLabel}
        >
          <ProjectHero
            src={media.cover.desktop}
            alt="Jiffy Shirts e-commerce storefront"
          />
        </ContainerScroll>

        <OverviewSection>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Client</OverviewLabel>
              <OverviewValue>{client.name}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Country</OverviewLabel>
              <OverviewValue>{client.country}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewRow>
            <OverviewItem>
              <OverviewLabel>Services provided</OverviewLabel>
              <OverviewValue>{services.join(", ")}</OverviewValue>
            </OverviewItem>
            <OverviewItem>
              <OverviewLabel>Industry</OverviewLabel>
              <OverviewValue>{industry}</OverviewValue>
            </OverviewItem>
          </OverviewRow>
          <OverviewItem>
            <OverviewLabel>Tech stack</OverviewLabel>
            <OverviewValue>{techStack.join(", ")}</OverviewValue>
          </OverviewItem>
          {links?.project ? (
            <OverviewItem>
              <OverviewLabel>Project link</OverviewLabel>
              <OverviewValue>
                <a
                  href={links.project}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {links.project}
                </a>
              </OverviewValue>
            </OverviewItem>
          ) : null}
        </OverviewSection>

        <Body>
          <section>
            <h4>About the product</h4>
            <p>
              Jiffy is a U.S. e-commerce platform for blank apparel and custom
              printing. The catalog covers DTF transfers, stickers, and
              on-demand printed goods for creators, small businesses, and
              enterprise clients. Customers browse, customize, and check out
              with prices and delivery timelines shown up front.
            </p>
            <p>
              I worked on the Rails storefront frontend: catalogs,
              customizations, cart, checkout-adjacent flows, accounts, and print
              workflows. It is a live production site, and the native apps load
              the same pages in a WebView.
            </p>
            <figure>
              <ContentImage
                src={projectImage("jiffy-dtf-design")}
                alt="Jiffy DTF design tool"
              />
              <ImageCaption>
                The DTF product flow, where customers upload art and preview
                transfers.
              </ImageCaption>
            </figure>
          </section>

          <section>
            <h4>The storefront I worked on</h4>
            <p>
              Pages already render on the server in Rails. Webpack packs and Vue
              islands add behavior after that. Desktop gets US or CA store
              packs. The mobile browser uses the same pages. The apps load those
              pages in a WebView with mobile packs and a JS bridge.
            </p>
          </section>

          <section>
            <h4>Engineering decisions</h4>

            <DecisionSection>
              <DecisionBlock>
                <DecisionTitle>
                  1. Rails SSR and client-specific packs
                </DecisionTitle>

                <p>
                  I worked on a multi-page Rails storefront. HTML comes from the
                  server. JS and CSS arrive as Webpack packs. Which pack you get
                  depends on the page (default, product, account, checkout) and
                  on the client (US store, CA store, or the mobile app).
                </p>

                <DecisionQuestion>
                  Product pages are expensive to render, and deploys change
                  asset filenames. Cached HTML cannot keep old pack URLs.
                </DecisionQuestion>

                <p>
                  A single bundle for every client would ship a lot of unused
                  code. Product detail pages use full-page HTML caching because
                  they are expensive to render. The cached HTML keeps a
                  placeholder for pack tags. When the page is served, those tags
                  are filled from the current manifest, on a cache hit and on a
                  miss, so the browser always gets the right files after a
                  deploy.
                </p>

                <p>
                  The head loads page CSS first, then polyfills, deferred
                  vendors, Vue vendors when the page needs them, analytics, the
                  page pack, and a separate Tailwind pack. Checkout still loads
                  webpack packs next to a leftover Angular runtime.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>What the storefront does</h6>
                    <ul>
                      <li>US / CA / mobile packs stay separate</li>
                      <li>PDP HTML can be cached without stale asset URLs</li>
                      <li>Pack tags are filled from the current manifest</li>
                      <li>Fits a live catalog with more than one client</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>One universal pack</h6>
                    <ul>
                      <li>Fewer entry points to manage</li>
                      <li>Easier to reason about at first</li>
                      <li>Ships unused code to every client</li>
                      <li>Fights locale and WebView differences</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  If I changed UI on a product page, I also had to think about
                  the cache key and how pack tags get attached on the way out.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>2. Critical CSS vs deferred packs</DecisionTitle>

                <p>
                  The storefront has a lot of Sass and Tailwind. If every
                  feature stylesheet loaded with the first paint, listing and
                  product pages would download CSS they do not need yet. A lot
                  of the frontend work was deciding what belonged in the main
                  pack and what could wait.
                </p>

                <DecisionQuestion>
                  Feature CSS should not block first paint on a live catalog.
                </DecisionQuestion>

                <p>
                  There is no critical-CSS extractor in the build. The main
                  application or PDP pack CSS stays blocking. Secondary packs
                  such as footer, payment logos, deliver-to, and cookie consent
                  load as print media and switch to all once they arrive. On
                  cached PDPs, deliver-to CSS uses a preload-as-style path
                  instead. A few masthead styles have to look right immediately,
                  so those SCSS files are imported into the main pack on
                  purpose.
                </p>

                <CssPathSplit />

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Defer secondary packs</h6>
                    <ul>
                      <li>Control per component pack</li>
                      <li>Little extra build tooling</li>
                      <li>Fits the existing Sass packs</li>
                      <li>Easy to miss a pack and get a flash of unstyled UI</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>Put it all in the main pack</h6>
                    <ul>
                      <li>One request to reason about</li>
                      <li>Blocks first paint with unused styles</li>
                      <li>Ties Tailwind and Sass together</li>
                      <li>Worse fit for route-specific UI</li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  On a given ticket I would ask whether a style belonged in the
                  main pack or could ship later with its feature. Layout shift
                  and first paint already mattered on those pages.
                </p>
              </DecisionBlock>

              <DecisionBlock>
                <DecisionTitle>
                  3. WebView client, shared commerce
                </DecisionTitle>

                <p>
                  The iOS and Android apps embed the same Rails storefront in a
                  WebView. I worked on the frontend in both places: mobile
                  packs, app chrome, and a bridge back to native features.
                </p>

                <DecisionQuestion>
                  A storefront change also has to work inside the app WebView.
                </DecisionQuestion>

                <p>
                  The server sees the app from headers, stores a device cookie,
                  and switches to mobile packs and a mobile-app layout class.
                  Version gates turn native features on or off, including Apple
                  Pay, filters, and PayPal. The page talks to the app with React
                  Native WebView postMessage for experiments, cart quantity,
                  payments, filters, and when the page is ready.
                </p>

                <p>
                  Pack choice, cache variation, and bridge events were part of
                  shipping UI that still worked in the app.
                </p>
              </DecisionBlock>

              <figure>
                <ContentImage
                  src={projectImage("jiffy-cart")}
                  alt="Jiffy cart page"
                />
                <ImageCaption>
                  Cart UI that had to stay correct on web and in the app
                  WebView.
                </ImageCaption>
              </figure>

              <DecisionBlock>
                <DecisionTitle>4. Vue islands over Rails HTML</DecisionTitle>

                <p>
                  Cart and some product-detail flows are islands. Rails still
                  renders the shell and the props. A deferred pack mounts Vue on
                  a DOM node. I worked in that pattern. The catalog stayed on
                  Rails HTML.
                </p>

                <DecisionQuestion>
                  Cart and DTF need a real client app. The catalog still needs
                  server HTML.
                </DecisionQuestion>

                <p>
                  An ERB template defines the mount node. Props come through
                  JSON attributes, window config, or slotted server HTML. Shared
                  Vue vendors load when needed, then the island pack calls
                  createApp and mount. Catalog pages stay server-rendered. Vue
                  only mounts on the flows that need it.
                </p>

                <DecisionComparison>
                  <DecisionCard>
                    <h6>Vue islands on the catalog</h6>
                    <ul>
                      <li>Fits the live Rails storefront</li>
                      <li>Keeps server HTML and PDP cache</li>
                      <li>Shares one Vue runtime via vendors</li>
                      <li>Means living with both ERB and Vue</li>
                    </ul>
                  </DecisionCard>

                  <DecisionCard>
                    <h6>React on Design Lab</h6>
                    <ul>
                      <li>Used for the customization lab, not the catalog</li>
                      <li>Own routing and a much larger client app</li>
                      <li>Leaves cart and DTF PDP on Vue</li>
                      <li>
                        Covered in the{" "}
                        <a
                          href="/projects/jiffy-design-lab"
                          className={getLinkClassName()}
                        >
                          Design Lab case study
                        </a>
                      </li>
                    </ul>
                  </DecisionCard>
                </DecisionComparison>

                <p>
                  Classic checkout can still load Angular next to webpack packs.
                  Shipping there meant living with a mixed runtime.
                </p>
              </DecisionBlock>
            </DecisionSection>
          </section>

          <section>
            <h4>Outcome</h4>
            <p>
              Catalog pages stay server-rendered and cacheable. Cart and the
              heavier product flows stay interactive through Vue. Web and the
              apps share one storefront, with different packs and a bridge when
              native behavior is required.
            </p>
            <p>
              Day to day I traced pricing mismatches from the product page
              through checkout, cut layout shift on product pages, removed old
              A/B code from the PDP, and shipped cookie consent and analytics
              without blocking browse or checkout. Those tickets had to work
              with packs, cache keys, WebView gates, and island boundaries.
            </p>
          </section>

          <section>
            <h4>Testimonials</h4>
            <Testimonials showMore={false} reviews={client.reviews} />
          </section>
        </Body>
      </PageContainer>
      <ContactMeSection />
    </>
  );
};

export default JiffyProjectSection;
